import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useDisclosure,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  Spinner,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  Divider,
  useColorModeValue,
  InputGroup,
  InputLeftElement,
  Input,
  Checkbox,
  TableContainer,
  SimpleGrid,
} from '@chakra-ui/react';
import { 
  FiPlus, 
  FiMoreVertical, 
  FiEdit2, 
  FiTrash2, 
  FiPercent,
  FiUsers,
  FiUser,
  FiAlertTriangle,
  FiSearch,
  FiCalendar,
  FiDollarSign
} from 'react-icons/fi';
import { useUserContext } from '../../../context/UserContext';
import {
  useGetPayrollAllowances,
  useCreatePayrollAllowance,
  useUpdatePayrollAllowance,
  useDeletePayrollAllowance,
} from '../../../services/payrollService';
import { useGetEmployees } from '../../../services/employeeService';
import { useGetPlanInfo } from '../../../services/planService';
import { formatCurrency } from '../../../utils/currency';
import MonthSelector from '../../../components/MonthSelector';
import YearSelector from '../../../components/yearSelector';

const PayrollAllowances = () => {
  const { user } = useUserContext();
  const toast = useToast();
  const { planInfo } = useGetPlanInfo();
  
  // State
  const [formData, setFormData] = useState({
    allowance_type: 'BONUS',
    calculation_type: 'PERCENTAGE',
    percentage_rate: 0,
    flat_rate_amount: 0,
    is_company_wide: true,
    employee_ids: [],
    month: null,
    year: null,
    recurrence_type: 'ONE_TIME'
  });
  
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [editingAllowance, setEditingAllowance] = useState(null);
  const [allowanceToDelete, setAllowanceToDelete] = useState(null);
  const [filterAllowanceType, setFilterAllowanceType] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterIsActive, setFilterIsActive] = useState('');
  const dropdownRef = useRef(null);
  
  // Modals
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  
  // API hooks
  const { 
    data: allowancesData, 
    isLoading: allowancesLoading, 
    refetch: refetchAllowances 
  } = useGetPayrollAllowances({
    ...(filterAllowanceType && { allowance_type: filterAllowanceType }),
    ...(filterMonth && { month: parseInt(filterMonth) }),
    ...(filterYear && { year: parseInt(filterYear) }),
    ...(filterIsActive !== '' && { is_active: filterIsActive === 'true' }),
  });
  
  const { 
    getEmployeesData: employeesData, 
    getEmployeesIsLoading: employeesLoading
  } = useGetEmployees({ enabled: true });
  
  // Process data
  const employees = employeesData?.employees || [];
  const allowances = allowancesData?.data || allowancesData || [];
  
  // Filter employees based on search term
  const filteredEmployees = employees.filter(employee =>
    employee.first_name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
    employee.last_name?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
    employee.employee_id?.toLowerCase().includes(employeeSearchTerm.toLowerCase()) ||
    `${employee.first_name} ${employee.last_name}`.toLowerCase().includes(employeeSearchTerm.toLowerCase())
  );
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowEmployeeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Helper functions
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleScopeChange = (isCompanyWide) => {
    setFormData(prev => ({
      ...prev,
      is_company_wide: isCompanyWide,
      employee_ids: isCompanyWide ? [] : prev.employee_ids
    }));
    if (isCompanyWide) {
      setSelectedEmployeeIds([]);
    }
  };
  
  const handleEmployeeToggle = (employeeId) => {
    setSelectedEmployeeIds(prev => {
      const newIds = prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId];
      
      setFormData(prevForm => ({
        ...prevForm,
        employee_ids: newIds
      }));
      
      return newIds;
    });
  };
  
  const handleSelectAllEmployees = () => {
    if (selectedEmployeeIds.length === filteredEmployees.length && filteredEmployees.length > 0) {
      setSelectedEmployeeIds([]);
      setFormData(prev => ({ ...prev, employee_ids: [] }));
    } else {
      const allIds = filteredEmployees.map(emp => emp.employee_id);
      setSelectedEmployeeIds(allIds);
      setFormData(prev => ({ ...prev, employee_ids: allIds }));
    }
  };
  
  const handleMonthChange = (selectedOption) => {
    if (selectedOption) {
      handleFormChange('month', selectedOption.value);
    }
  };
  
  const handleYearChange = (selectedOption) => {
    if (selectedOption) {
      handleFormChange('year', selectedOption.value);
    }
  };
  
  // Create/Update mutations
  const { createAllowancePayload: createAllowanceMutation, createAllowanceIsLoading: isCreating } = useCreatePayrollAllowance(() => {
    onCreateClose();
    resetForm();
    refetchAllowances();
  });
  
  const { updateAllowancePayload: updateAllowanceMutation, updateAllowanceIsLoading: isUpdating } = useUpdatePayrollAllowance(() => {
    onCreateClose();
    setEditingAllowance(null);
    resetForm();
    refetchAllowances();
  });
  
  const { deleteAllowancePayload, deleteAllowanceIsLoading: isDeleting } = useDeletePayrollAllowance(() => {
    onDeleteClose();
    setAllowanceToDelete(null);
    refetchAllowances();
  });
  
  const resetForm = () => {
    setFormData({
      allowance_type: 'BONUS',
      calculation_type: 'PERCENTAGE',
      percentage_rate: 0,
      flat_rate_amount: 0,
      is_company_wide: true,
      employee_ids: [],
      month: null,
      year: null,
      recurrence_type: 'ONE_TIME'
    });
    setSelectedEmployeeIds([]);
    setEmployeeSearchTerm('');
  };
  
  const handleSubmitAllowance = () => {
    // Validation
    if (!formData.month || !formData.year) {
      toast({
        title: 'Validation Error',
        description: 'Please select month and year',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }
    
    if (formData.calculation_type === 'PERCENTAGE' && (!formData.percentage_rate || formData.percentage_rate <= 0)) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a valid percentage rate greater than 0',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }
    
    if (formData.calculation_type === 'FLAT_RATE' && (!formData.flat_rate_amount || formData.flat_rate_amount <= 0)) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a valid flat rate amount greater than 0',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }
    
    if (!formData.is_company_wide && (!formData.employee_ids || formData.employee_ids.length === 0)) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one employee for employee-specific allowance',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }
    
    const payload = {
      allowance_type: formData.allowance_type,
      calculation_type: formData.calculation_type,
      ...(formData.calculation_type === 'PERCENTAGE' 
        ? { percentage_rate: formData.percentage_rate }
        : { flat_rate_amount: formData.flat_rate_amount }),
      is_company_wide: formData.is_company_wide,
      ...(formData.is_company_wide ? {} : { employee_ids: formData.employee_ids }),
      month: formData.month,
      year: formData.year,
      recurrence_type: formData.recurrence_type
    };
    
    if (editingAllowance) {
      updateAllowanceMutation({
        allowanceId: editingAllowance.id,
        payload: payload
      });
    } else {
      createAllowanceMutation(payload);
    }
  };
  
  const handleEditAllowance = (allowance) => {
    setEditingAllowance(allowance);
    setFormData({
      allowance_type: allowance.allowance_type,
      calculation_type: allowance.calculation_type,
      percentage_rate: allowance.percentage_rate || 0,
      flat_rate_amount: allowance.flat_rate_amount || 0,
      is_company_wide: allowance.is_company_wide,
      employee_ids: allowance.employee_ids || [],
      month: allowance.month,
      year: allowance.year,
      recurrence_type: allowance.recurrence_type || 'ONE_TIME'
    });
    setSelectedEmployeeIds(allowance.employee_ids || []);
    onCreateOpen();
  };
  
  const handleDeleteAllowance = (allowance) => {
    setAllowanceToDelete(allowance);
    onDeleteOpen();
  };
  
  const confirmDelete = () => {
    if (allowanceToDelete) {
      deleteAllowancePayload(allowanceToDelete.id);
    }
  };
  
  // Helper functions for display
  const getAllowanceTypeColor = (type) => {
    return type === 'BONUS' ? 'blue' : 'green';
  };
  
  const getCalculationTypeColor = (type) => {
    return type === 'PERCENTAGE' ? 'purple' : 'orange';
  };
  
  const getRecurrenceColor = (type) => {
    return type === 'RECURRENT' ? 'teal' : 'gray';
  };
  
  const getScopeColor = (isCompanyWide) => {
    return isCompanyWide ? 'purple' : 'orange';
  };
  
  const formatMonthYear = (month, year) => {
    if (!month || !year) return '-';
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[month - 1]} ${year}`;
  };
  
  const getSelectedMonth = () => {
    if (!formData.month) return null;
    const monthNames = [
      {label:"January", value:1}, {label:"February", value:2}, {label:"March", value:3},
      {label:"April", value:4}, {label:"May", value:5}, {label:"June", value:6},
      {label:"July", value:7}, {label:"August", value:8}, {label:"September", value:9},
      {label:"October", value:10}, {label:"November", value:11}, {label:"December", value:12}
    ];
    return monthNames.find(m => m.value === formData.month) || null;
  };
  
  const getSelectedYear = () => {
    if (!formData.year) return null;
    return { label: formData.year, value: formData.year };
  };
  
  // Theme colors
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  
  if (allowancesLoading) {
    return (
      <Box p={6} bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor}>
        <Flex justify="center" align="center" h="200px">
          <Spinner size="xl" />
        </Flex>
      </Box>
    );
  }
  
  return (
    <Box p={6} bg={bgColor} borderRadius="lg" border="1px" borderColor={borderColor}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="lg" color="blue.600">
              Payroll Allowances
            </Heading>
            <Text color="gray.600">
              Manage BONUS and LEAVE_ALLOWANCE payments for employees
            </Text>
          </VStack>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={() => {
              resetForm();
              onCreateOpen();
            }}
          >
            Add Allowance
          </Button>
        </Flex>
        
        <Divider />
        
        {/* Filters */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <FormControl>
            <FormLabel>Filter by Type</FormLabel>
            <Select
              value={filterAllowanceType}
              onChange={(e) => setFilterAllowanceType(e.target.value)}
              placeholder="All Types"
            >
              <option value="BONUS">BONUS</option>
              <option value="LEAVE_ALLOWANCE">LEAVE_ALLOWANCE</option>
            </Select>
          </FormControl>
          
          <FormControl>
            <FormLabel>Filter by Month</FormLabel>
            <Select
              value={filterMonth}
              onChange={(e) => setFilterMonth(e.target.value)}
              placeholder="All Months"
            >
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                <option key={m} value={m}>
                  {new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </Select>
          </FormControl>
          
          <FormControl>
            <FormLabel>Filter by Year</FormLabel>
            <Input
              type="number"
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
              placeholder="Year"
              min="2000"
              max={new Date().getFullYear()}
            />
          </FormControl>
          
          <FormControl>
            <FormLabel>Filter by Status</FormLabel>
            <Select
              value={filterIsActive}
              onChange={(e) => setFilterIsActive(e.target.value)}
              placeholder="All Status"
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </Select>
          </FormControl>
        </SimpleGrid>
        
        {(filterAllowanceType || filterMonth || filterYear || filterIsActive) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilterAllowanceType('');
              setFilterMonth('');
              setFilterYear('');
              setFilterIsActive('');
            }}
          >
            Clear Filters
          </Button>
        )}
        
        {/* Allowances Table */}
        {allowances && Array.isArray(allowances) && allowances.length > 0 ? (
          <Box overflowX="auto">
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Allowance Type</Th>
                  <Th>Calculation</Th>
                  <Th>Scope</Th>
                  <Th>Month/Year</Th>
                  <Th>Recurrence</Th>
                  <Th>Employees</Th>
                  <Th>Status</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {allowances.map((allowance) => (
                  <Tr key={allowance.id}>
                    <Td>
                      <Badge colorScheme={getAllowanceTypeColor(allowance.allowance_type)}>
                        {allowance.allowance_type}
                      </Badge>
                    </Td>
                    <Td>
                      <VStack align="start" spacing={0}>
                        <Badge colorScheme={getCalculationTypeColor(allowance.calculation_type)} variant="subtle">
                          {allowance.calculation_type}
                        </Badge>
                        <Text fontSize="sm" color="gray.600">
                          {allowance.calculation_type === 'PERCENTAGE' 
                            ? `${allowance.percentage_rate}%`
                            : formatCurrency(allowance.flat_rate_amount || 0, planInfo?.country)}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Badge colorScheme={getScopeColor(allowance.is_company_wide)}>
                        {allowance.is_company_wide ? (
                          <HStack spacing={1}>
                            <FiUsers />
                            <Text>Company-wide</Text>
                          </HStack>
                        ) : (
                          <HStack spacing={1}>
                            <FiUser />
                            <Text>Employee-specific</Text>
                          </HStack>
                        )}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontWeight="600">
                        {formatMonthYear(allowance.month, allowance.year)}
                      </Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={getRecurrenceColor(allowance.recurrence_type)} variant="subtle">
                        {allowance.recurrence_type === 'RECURRENT' ? 'Recurrent' : 'One-time'}
                      </Badge>
                    </Td>
                    <Td>
                      {allowance.is_company_wide ? (
                        <Text fontSize="sm" color="gray.500">All Employees</Text>
                      ) : (
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm" fontWeight="600">
                            {allowance.employee_ids?.length || 0} employee(s)
                          </Text>
                          {allowance.employee_names && allowance.employee_names.length > 0 && (
                            <Text fontSize="xs" color="gray.500">
                              {allowance.employee_names.slice(0, 2).join(', ')}
                              {allowance.employee_names.length > 2 && ` +${allowance.employee_names.length - 2} more`}
                            </Text>
                          )}
                        </VStack>
                      )}
                    </Td>
                    <Td>
                      <Badge colorScheme={allowance.is_active ? 'green' : 'red'}>
                        {allowance.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </Td>
                    <Td>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          icon={<FiMoreVertical />}
                          variant="ghost"
                          size="sm"
                        />
                        <MenuList>
                          <MenuItem
                            icon={<FiEdit2 />}
                            onClick={() => handleEditAllowance(allowance)}
                          >
                            Edit
                          </MenuItem>
                          <MenuItem
                            icon={<FiTrash2 />}
                            color="red.500"
                            onClick={() => handleDeleteAllowance(allowance)}
                          >
                            Deactivate
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        ) : (
          <Box textAlign="center" py={10}>
            <FiAlertTriangle size={48} color="gray" />
            <Text mt={4} color="gray.600">
              No payroll allowances found. Create your first allowance to get started.
            </Text>
          </Box>
        )}
        
        {/* Create/Edit Allowance Modal */}
        <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              {editingAllowance ? 'Edit Allowance' : 'Add New Allowance'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Allowance Type</FormLabel>
                  <Select
                    value={formData.allowance_type}
                    onChange={(e) => handleFormChange('allowance_type', e.target.value)}
                  >
                    <option value="BONUS">BONUS</option>
                    <option value="LEAVE_ALLOWANCE">LEAVE_ALLOWANCE</option>
                  </Select>
                </FormControl>
                
                <FormControl>
                  <FormLabel>Calculation Type</FormLabel>
                  <Select
                    value={formData.calculation_type}
                    onChange={(e) => handleFormChange('calculation_type', e.target.value)}
                  >
                    <option value="PERCENTAGE">Percentage</option>
                    <option value="FLAT_RATE">Flat Rate</option>
                  </Select>
                </FormControl>
                
                {formData.calculation_type === 'PERCENTAGE' ? (
                  <FormControl>
                    <FormLabel>Percentage Rate (%)</FormLabel>
                    <NumberInput
                      value={formData.percentage_rate}
                      onChange={(value) => handleFormChange('percentage_rate', parseFloat(value) || 0)}
                      min={0}
                      max={100}
                      precision={2}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                ) : (
                  <FormControl>
                    <FormLabel>Flat Rate Amount</FormLabel>
                    <NumberInput
                      value={formData.flat_rate_amount}
                      onChange={(value) => handleFormChange('flat_rate_amount', parseFloat(value) || 0)}
                      min={0}
                      precision={2}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                )}
                
                <FormControl>
                  <FormLabel>Scope</FormLabel>
                  <Select
                    value={formData.is_company_wide ? 'company' : 'employee'}
                    onChange={(e) => handleScopeChange(e.target.value === 'company')}
                  >
                    <option value="company">Company-wide</option>
                    <option value="employee">Employee-specific</option>
                  </Select>
                </FormControl>
                
                {!formData.is_company_wide && (
                  <FormControl>
                    <FormLabel>Select Employees</FormLabel>
                    <Box position="relative" ref={dropdownRef}>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none">
                          <FiSearch color="gray.300" />
                        </InputLeftElement>
                        <Input
                          value={employeeSearchTerm}
                          onChange={(e) => {
                            setEmployeeSearchTerm(e.target.value);
                            setShowEmployeeDropdown(true);
                          }}
                          onFocus={() => setShowEmployeeDropdown(true)}
                          placeholder="Search employees by name or ID..."
                        />
                      </InputGroup>
                      
                      {showEmployeeDropdown && filteredEmployees.length > 0 && (
                        <Box
                          position="absolute"
                          top="100%"
                          left={0}
                          right={0}
                          bg="white"
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          boxShadow="lg"
                          zIndex={1000}
                          maxHeight="300px"
                          overflowY="auto"
                          mt={1}
                        >
                          <Box p={2} bg="gray.50" borderBottom="1px solid" borderColor="gray.200" position="sticky" top={0}>
                            <Checkbox
                              isChecked={selectedEmployeeIds.length === filteredEmployees.length && filteredEmployees.length > 0}
                              isIndeterminate={selectedEmployeeIds.length > 0 && selectedEmployeeIds.length < filteredEmployees.length}
                              onChange={handleSelectAllEmployees}
                            >
                              <Text fontSize="sm" fontWeight="600">
                                Select All ({filteredEmployees.length})
                              </Text>
                            </Checkbox>
                          </Box>
                          {filteredEmployees.slice(0, 20).map((employee) => (
                            <Box
                              key={employee.employee_id}
                              p={3}
                              borderBottom="1px solid"
                              borderColor="gray.100"
                              _hover={{ bg: "gray.50" }}
                            >
                              <Checkbox
                                isChecked={selectedEmployeeIds.includes(employee.employee_id)}
                                onChange={() => handleEmployeeToggle(employee.employee_id)}
                              >
                                <VStack align="start" spacing={0}>
                                  <Text fontWeight="medium">
                                    {employee.first_name} {employee.last_name}
                                  </Text>
                                  <Text fontSize="sm" color="gray.600">
                                    {employee.employee_id}
                                  </Text>
                                </VStack>
                              </Checkbox>
                            </Box>
                          ))}
                        </Box>
                      )}
                      
                      {selectedEmployeeIds.length > 0 && (
                        <Box mt={2} p={2} bg="blue.50" borderRadius="md">
                          <Text fontSize="sm" fontWeight="600" color="blue.700">
                            Selected: {selectedEmployeeIds.length} employee(s)
                          </Text>
                        </Box>
                      )}
                    </Box>
                  </FormControl>
                )}
                
                <Divider />
                
                <SimpleGrid columns={2} spacing={4} w="full">
                  <FormControl>
                    <FormLabel>Month</FormLabel>
                    <MonthSelector
                      selectedYear={formData.year}
                      onChange={handleMonthChange}
                    />
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel>Year</FormLabel>
                    <YearSelector
                      selectedYear={formData.year}
                      onChange={handleYearChange}
                    />
                  </FormControl>
                </SimpleGrid>
                
                {formData.month && formData.year && (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <Text fontSize="sm">
                      Period: {formatMonthYear(formData.month, formData.year)}
                    </Text>
                  </Alert>
                )}
                
                <FormControl>
                  <FormLabel>Recurrence Type</FormLabel>
                  <Select
                    value={formData.recurrence_type}
                    onChange={(e) => handleFormChange('recurrence_type', e.target.value)}
                  >
                    <option value="ONE_TIME">One-time (applies only for specified month/year)</option>
                    <option value="RECURRENT">Recurrent (applies every month/year)</option>
                  </Select>
                </FormControl>
                
                <HStack spacing={4} w="full">
                  <Button
                    colorScheme="blue"
                    onClick={handleSubmitAllowance}
                    isLoading={isCreating || isUpdating}
                    w="full"
                  >
                    {editingAllowance ? 'Update Allowance' : 'Create Allowance'}
                  </Button>
                  <Button onClick={onCreateClose} w="full">
                    Cancel
                  </Button>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
        
        {/* Delete Confirmation Modal */}
        <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Deactivate Allowance</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text>
                Are you sure you want to deactivate this allowance? This will prevent it from being included in future payroll calculations.
              </Text>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={confirmDelete}
                isLoading={isDeleting}
              >
                Deactivate
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};

export default PayrollAllowances;

